import React from 'react';
import {Box, Flex, Grid, Switch, Text, Tooltip} from '@chakra-ui/react';
import {
  IntegrationType,
  FieldMappingLabels,
  ManagedFieldMappingField,
  ManagedFieldMappingCrmLabels,
  ManagedFieldMappingCrmNames,
  OptionalManagedFieldMappingFields,
  CoreManagedFieldMappingFields,
  isManagedFieldMappingField,
  isAdditionalFieldMapping,
} from '../../shared/integrations';

import {
  GridCell,
  GridHeader,
  IntegrationSection,
  gridTemplateColumns,
} from './components';
import {ChevronRight} from '@carbon/icons-react';
import {useIntegrationDetailsState} from './IntegrationDetails.state';
import _ from 'lodash';
import {
  useIntegrationFromContext,
  useIntegrationStaticInfo,
} from './IntegrationDetailsContext';
import {ObjectId} from 'bson';

export const ManagedEnrichmentFieldMapping = () => {
  const integration = useIntegrationFromContext();
  const integrationInfo = useIntegrationStaticInfo();

  const {additionalFieldMappings, managedFieldMappings, serverState} =
    useIntegrationDetailsState();

  const availableFieldMappingFields = CoreManagedFieldMappingFields.concat(
    _.intersection(
      (serverState?.config?.optionalFields ?? []).filter(
        isManagedFieldMappingField
      ),
      OptionalManagedFieldMappingFields
    )
  );

  return (
    <IntegrationSection>
      <Box fontSize={'lg'} fontWeight={500} mb={6}>
        Managed Field Mappings
      </Box>
      <Text mb={10}>
        When enabling a managed field, Keyplay will create the field
        automatically on your {integrationInfo.label} company record within a
        “Keyplay Enrichment” field group. Values are refreshed weekly.
      </Text>

      <Grid templateColumns={gridTemplateColumns} gap={2}>
        <GridHeader></GridHeader>
        <GridHeader>Keyplay Field</GridHeader>
        <GridHeader></GridHeader>
        <GridHeader>{integrationInfo.label} Field</GridHeader>

        {availableFieldMappingFields.map((fieldName) => {
          const crmFieldName = getCrmFieldName(fieldName, integration.type);
          const fieldMappingId = managedFieldMappings.find(
            (fieldMapping) => fieldMapping.enrichmentField === fieldName
          )?.id;

          return (
            <ManagedEnrichmentFieldGridRow
              key={fieldName}
              fieldName={fieldName}
              crmFieldName={crmFieldName}
              fieldMappingId={fieldMappingId}
              hasExistingMapping={additionalFieldMappings.some(
                (fieldMapping) =>
                  isAdditionalFieldMapping(fieldMapping) &&
                  fieldMapping.crmFieldName === crmFieldName
              )}
            />
          );
        })}
      </Grid>
    </IntegrationSection>
  );
};

const CrmFieldCell = ({name, label}: {name: string; label: string}) => {
  return (
    <Flex direction="column">
      <Tooltip label={name} placement="top">
        <Box>{label}</Box>
      </Tooltip>
    </Flex>
  );
};

const getCrmFieldName = (
  fieldMappingField: ManagedFieldMappingField,
  integrationName: IntegrationType
): string => {
  const fieldValue = ManagedFieldMappingCrmNames[fieldMappingField];

  if (integrationName === 'salesforce') {
    // Convert string format for Salesforce
    // Camel case (with first work capitalized) and add __c
    const words = fieldValue.split('_');
    const capitalizedWords = words.map(
      (word) => word.charAt(0).toUpperCase() + word.slice(1)
    );
    return capitalizedWords.join('') + '__c';
  }

  // Return original string for HubSpot
  return fieldValue;
};

const ManagedEnrichmentFieldGridRow = ({
  fieldName,
  crmFieldName,
  fieldMappingId,
  hasExistingMapping,
}: {
  fieldName: ManagedFieldMappingField;
  crmFieldName: string;
  fieldMappingId?: ObjectId;
  hasExistingMapping?: boolean;
}) => {
  const {addManagedFieldMapping, deleteManagedFieldMapping, publishChanges} =
    useIntegrationDetailsState();
  return (
    <>
      <GridCell>
        <Tooltip
          isDisabled={!hasExistingMapping}
          label={'This field is already used as an additional field mapping.'}
          placement="top"
          shouldWrapChildren
        >
          <Switch
            isChecked={!!fieldMappingId}
            isDisabled={hasExistingMapping}
            onChange={(event) => {
              if (event.target.checked) {
                addManagedFieldMapping({
                  id: new ObjectId(),
                  enrichmentField: fieldName,
                  crmFieldName,
                  managementType: 'managed',
                  kind: 'keyplay',
                  updatedAt: new Date(),
                });
                publishChanges.mutate();
              } else {
                if (!fieldMappingId) {
                  return;
                }

                deleteManagedFieldMapping(fieldMappingId);
                publishChanges.mutate();
              }
            }}
          />
        </Tooltip>
      </GridCell>
      <GridCell hasBorder>{FieldMappingLabels[fieldName]}</GridCell>
      <GridCell>
        <ChevronRight />
      </GridCell>
      <GridCell hasBorder>
        <CrmFieldCell
          name={crmFieldName}
          label={ManagedFieldMappingCrmLabels[fieldName]}
        />
      </GridCell>
    </>
  );
};
